/**
 * AP-39 12.2 — **Posteingang** (`/inbox`): alles, was von außen kommt, in einer Liste
 * im Listen-Standard (FilterBar, Spalten, Sortierung, HelpPopover). Die Einträge einer
 * Mail stehen als aufklappbare Gruppe beieinander; ein Klick öffnet Vorschau und
 * Seitenfeld rechts. Die ganze Seite ist Ablagefläche (5.1).
 *
 * Phase A: ablegen, ansehen, Art und Anmerkung, ignorieren, Papierkorb. Phase B: Spalten Partner,
 * Zuordnung und Zuständig, Mehrfachauswahl (ein Ziel für alle, ignorieren, löschen). Bezug (gefundene
 * Belegnummern) kommt mit Phase C.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useAenderung } from '@/lib/assistent/assistent-beilagen'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronDown, ChevronRight, EyeOff, FolderInput, Inbox, Link2, Mail, Settings, SlidersHorizontal, Trash2, Upload, X } from 'lucide-react'
import { INBOX_FLAGS, INBOX_KINDS, INBOX_STATUSES, INBOX_SOURCES, erfuelltRecht } from '@symphenia/shared-types'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { PapierkorbFenster, type PapierkorbEintrag } from '@/components/Papierkorb'
import { ablageflaeche, usePosteingang } from '@/components/eingang/posteingang'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige } from '@/lib/dialoge'
import { useAuthStore } from '@/lib/auth.store'
import { pageWide } from '@/lib/form-styles'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { assignInboxItem, deleteInboxItem, fetchInbox, restoreInboxItem, setInboxIgnored } from '../api/inbox.api'
import { EintragSeitenfeld } from '../components/EintragSeitenfeld'
import { ZielSuche } from '../components/ZielSuche'
import { ART_TEXT, KENNZEICHEN_TEXT, STATUS_TEXT, WEG_TEXT, eingangText, groesseText } from '../lib/texte'
import type { InboxItem, ListInboxParams } from '../types/inbox.types'

interface ColDef {
  key: string
  label: string
  align?: 'right'
  /** Schlüssel für die Sortierung am Server; ohne Angabe nicht sortierbar */
  sort?: string
}
const ALLE_SPALTEN: ColDef[] = [
  { key: 'source', label: 'Weg' },
  { key: 'sender', label: 'Absender', sort: 'senderAddress' },
  { key: 'name', label: 'Betreff bzw. Datei', sort: 'fileName' },
  { key: 'kind', label: 'Art', sort: 'kind' },
  { key: 'partner', label: 'Partner' },
  { key: 'reference', label: 'Bezug' },
  { key: 'assignment', label: 'Zuordnung' },
  { key: 'status', label: 'Status', sort: 'status' },
  { key: 'responsible', label: 'Zuständig' },
  { key: 'size', label: 'Größe', align: 'right' },
  { key: 'note', label: 'Anmerkung' },
]
const DEFAULT_KEYS = ['sender', 'name', 'kind', 'partner', 'reference', 'assignment', 'status']
const LAYOUT_KEY = 'columnbar_layout_inbox'
interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
}
const VORGABE: ColumnState = { columnKeys: DEFAULT_KEYS, columnWidths: {}, sortKey: 'receivedAt', sortDir: 'desc' }
const SEITE = 100

const FILTER_FELDER: FilterFieldDef[] = [
  { key: 'q', label: 'Suche (Datei, Betreff, Absender, Anmerkung)', type: 'text', category: 'core', operators: ['contains'] },
  {
    key: 'status',
    label: 'Status',
    type: 'select-multi',
    category: 'core',
    operators: ['is', 'is_not'],
    options: INBOX_STATUSES.map((s) => ({ value: s, label: STATUS_TEXT[s].label })),
  },
  { key: 'kind', label: 'Art', type: 'select-multi', category: 'core', operators: ['is'], options: INBOX_KINDS.map((k) => ({ value: k, label: ART_TEXT[k] })) },
  { key: 'source', label: 'Weg', type: 'select-multi', category: 'core', operators: ['is'], options: INBOX_SOURCES.map((s) => ({ value: s, label: WEG_TEXT[s] })) },
  { key: 'mine', label: 'nur meine', type: 'boolean', category: 'core', operators: ['is'] },
  { key: 'from', label: 'eingegangen ab', type: 'date', category: 'core', operators: ['is'] },
  { key: 'to', label: 'eingegangen bis', type: 'date', category: 'core', operators: ['is'] },
  { key: 'all', label: 'auch ältere Erledigte', type: 'boolean', category: 'core', operators: ['is'] },
  { key: 'flag', label: 'Kennzeichen', type: 'select', category: 'core', operators: ['is'], options: INBOX_FLAGS.map((f) => ({ value: f, label: KENNZEICHEN_TEXT[f] ?? f })) },
]

function filterParams(f: Record<string, ActiveFilter>): ListInboxParams {
  const p: ListInboxParams = {}
  for (const [key, a] of Object.entries(f)) {
    if (!a.value) continue
    if (key === 'q') p.q = a.value
    if (key === 'status') {
      p.status = a.value
      if (a.operator === 'is_not') p.statusNot = true
    }
    if (key === 'kind') p.kind = a.value
    if (key === 'source') p.source = a.value
    if (key === 'mine' && a.value === 'true') p.mine = true
    if (key === 'from') p.from = a.value.slice(0, 10)
    if (key === 'to') p.to = a.value.slice(0, 10)
    if (key === 'flag') p.flag = a.value
    if (key === 'all' && a.value === 'true') p.all = true
  }
  return p
}

/** Zeilen einer Mail beieinander: in der Reihenfolge des ersten Auftretens, die Gruppe als Block. */
type Zeile = { art: 'einzeln'; eintrag: InboxItem } | { art: 'mail'; groupId: string; eintraege: InboxItem[] }
function gruppiere(rows: InboxItem[]): Zeile[] {
  const gruppen = new Map<string, InboxItem[]>()
  for (const r of rows) gruppen.set(r.groupId, [...(gruppen.get(r.groupId) ?? []), r])
  const gesehen = new Set<string>()
  const aus: Zeile[] = []
  for (const r of rows) {
    if (gesehen.has(r.groupId)) continue
    gesehen.add(r.groupId)
    const g = gruppen.get(r.groupId)!
    if (g.length > 1) aus.push({ art: 'mail', groupId: r.groupId, eintraege: g })
    else aus.push({ art: 'einzeln', eintrag: r })
  }
  return aus
}

export default function InboxPage() {
  // Ein Klick auf das Symbol im Menükopf, während die Seite offen ist, setzt die Filter neu.
  const { search } = useLocation()
  return <InboxListe key={search} />
}

const KENNUNG = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function InboxListe() {
  const { user } = useAuthStore()
  const darf = erfuelltRecht('inbox.item.manage', user?.permissions)
  const einstellungen = erfuelltRecht('inbox.settings.manage', user?.permissions)
  const [suchParameter] = useSearchParams()
  const [adresse] = useState(() => filterAusAdresse(suchParameter, FILTER_FELDER))
  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(() => ({ ...VORGABE, ...loadUiSetting<Partial<ColumnState> | null>(LAYOUT_KEY, null) }))
  const [config, setConfig] = useState(false)
  const [rows, setRows] = useState<InboxItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // AP-18 Phase F: `?eintrag=<Kennung>` öffnet das Seitenfeld eines Eintrags — der Sprung aus einer Antwort des Assistenten.
  const [offen, setOffen] = useState<string | null>(() => (KENNUNG.test(suchParameter.get('eintrag') ?? '') ? suchParameter.get('eintrag') : null))
  const [zu, setZu] = useState<Set<string>>(new Set())
  const [ziehen, setZiehen] = useState(false)
  const [korb, setKorb] = useState(false)
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [zuordnenOffen, setZuordnenOffen] = useState(false)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const ablegen = usePosteingang((s) => s.ablegen)
  const stand = usePosteingang((s) => s.stand)
  const zaehlerNeu = usePosteingang((s) => s.zaehlerNeu)
  // 5.2-5: arbeitet der Ordner nicht, steht es über der Liste.
  const ordnerStoerung = usePosteingang((s) => (s.zaehler?.warning && s.zaehler.folderError ? s.zaehler : null))
  const datei = useRef<HTMLInputElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragKey = useRef<string | null>(null)
  const [dragOver, setDragOver] = useState<string | null>(null)
  const aktuell = useRef({ filterValues, colState })
  aktuell.current = { filterValues, colState }

  const laden = useCallback(async (f: Record<string, ActiveFilter>, s: ColumnState) => {
    setLoading(true)
    setError(null)
    try {
      const r = await fetchInbox({ ...filterParams(f), sortBy: s.sortKey, sortDir: s.sortDir, limit: SEITE })
      setRows(r.data)
      setTotal(r.meta.total)
      setAuswahl((alt) => new Set([...alt].filter((id) => r.data.some((x) => x.id === id))))
    } catch (e) {
      setError(apiErrorText(e, 'Der Posteingang ließ sich nicht laden.'))
    } finally {
      setLoading(false)
    }
  }, [])
  const neu = useCallback(() => void laden(aktuell.current.filterValues, aktuell.current.colState), [laden])
  // In der Leiste des Assistenten wurde eine Zuordnung übernommen: Liste und Zähler stimmen nicht mehr.
  const nachAssistent = useCallback(() => {
    neu()
    void zaehlerNeu()
  }, [neu, zaehlerNeu])
  useAenderung('werkzeug', 'zuordnung_vorschlagen', nachAssistent)
  // Neu laden nach jedem Ablegen — auch über Symbol oder Menüpunkt.
  useEffect(() => {
    neu()
  }, [stand, neu])

  const onFilter = useCallback(
    (f: Record<string, ActiveFilter>) => {
      setFilterValues(f)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => void laden(f, aktuell.current.colState), 400)
    },
    [laden],
  )
  const setze = (u: (p: ColumnState) => ColumnState) =>
    setColState((p) => {
      const n = u(p)
      saveUiSetting(LAYOUT_KEY, n)
      return n
    })
  const sortiere = (key: string) => {
    const n = { ...colState, sortKey: key, sortDir: colState.sortKey === key && colState.sortDir === 'asc' ? ('desc' as const) : ('asc' as const) }
    setze(() => n)
    void laden(filterValues, n)
  }
  const breite = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const start = (e.target as HTMLElement).closest('th')?.getBoundingClientRect().width ?? 120
    const x = e.clientX
    const move = (ev: MouseEvent) => setze((p) => ({ ...p, columnWidths: { ...p.columnWidths, [key]: Math.max(60, start + ev.clientX - x) } }))
    const up = () => {
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
    }
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  }

  const spalten = colState.columnKeys.map((k) => ALLE_SPALTEN.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const hinzu = ALLE_SPALTEN.filter((c) => !colState.columnKeys.includes(c.key))
  const zeilen = useMemo(() => gruppiere(rows), [rows])
  const pfeil = (key?: string) =>
    !key ? null : colState.sortKey === key ? colState.sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" /> : <ArrowUpDown className="h-3 w-3 opacity-30" />
  const gesamt = 2 + spalten.length + (config && hinzu.length ? 1 : 0) + 1
  const gewaehlt = rows.filter((x) => auswahl.has(x.id))
  const waehle = (id: string, an: boolean) =>
    setAuswahl((alt) => {
      const n = new Set(alt)
      if (an) n.add(id)
      else n.delete(id)
      return n
    })
  /** Eine Handlung für jede gewählte Zeile — Fehler einzeln gezählt, die übrigen laufen weiter. */
  const fuerAlle = async (was: string, schritt: (a: InboxItem) => Promise<unknown>) => {
    let fehler = 0
    for (const a of gewaehlt) await schritt(a).catch(() => (fehler += 1))
    setHinweis(`${was}: ${gewaehlt.length - fehler} von ${gewaehlt.length}${fehler ? ` — ${fehler} ging nicht` : ''}`)
    setAuswahl(new Set())
    neu()
    void zaehlerNeu()
  }
  const loeschenAlle = async () => {
    if (!(await bestaetige(`${gewaehlt.length} Einträge in den Papierkorb legen? Sie bleiben dort 30 Tage.`, { ok: 'In den Papierkorb', gefaehrlich: true }))) return
    await fuerAlle('In den Papierkorb', (a) => deleteInboxItem(a.id))
  }

  const zelle = (a: InboxItem, key: string): React.ReactNode => {
    switch (key) {
      case 'source':
        return WEG_TEXT[a.source]
      case 'sender':
        return a.senderName || a.senderAddress || ''
      case 'name':
        return (
          <span className="inline-flex items-center gap-1.5">
            {a.isMailText && <Mail className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-label="Text der E-Mail" />}
            <span className="truncate">{a.fileName}</span>
          </span>
        )
      case 'kind':
        return a.kind ? ART_TEXT[a.kind] : ''
      case 'status':
        return (
          <span className="inline-flex flex-wrap items-center gap-1">
            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TEXT[a.status].klasse}`}>{STATUS_TEXT[a.status].label}</span>
            {a.flags.map((f) => (
              <span key={f} className="inline-flex rounded-full border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">
                {KENNZEICHEN_TEXT[f] ?? f}
              </span>
            ))}
          </span>
        )
      case 'partner':
        return a.partnerName ?? ''
      case 'reference':
        return [...new Set((a.references ?? []).map((f) => f.documentNumber))].join(', ')
      case 'assignment':
        return (
          <span className="inline-flex flex-wrap gap-x-2" onClick={(ev) => ev.stopPropagation()}>
            {a.assignments.map((z) =>
              z.url ? (
                <Link key={z.id} to={z.url} className="text-primary hover:underline">
                  {z.targetLabel}
                </Link>
              ) : (
                <span key={z.id}>{z.targetLabel}</span>
              ),
            )}
          </span>
        )
      case 'responsible':
        return a.responsibleName ?? ''
      case 'size':
        return groesseText(a.sizeBytes)
      case 'note':
        return a.note ?? ''
      default:
        return null
    }
  }

  const zeileKlasse = (id: string, i: number) =>
    `cursor-pointer border-b border-border last:border-0 ${i % 2 === 0 ? 'bg-background' : 'bg-muted/20'} ${offen === id ? '!bg-primary/10' : ''} hover:bg-accent/30`
  const eintragZeile = (a: InboxItem, i: number, eingerueckt = false) => (
    <tr key={a.id} className={zeileKlasse(a.id, i)} onClick={() => setOffen(a.id)}>
      <td className="w-8 px-2 py-1.5" onClick={(ev) => ev.stopPropagation()}>
        {darf && <input type="checkbox" aria-label={a.fileName} checked={auswahl.has(a.id)} onChange={(ev) => waehle(a.id, ev.target.checked)} />}
      </td>
      <td className={`whitespace-nowrap py-1.5 tabular-nums text-muted-foreground ${eingerueckt ? 'pl-9 pr-3' : 'px-3'}`}>{eingangText(a.receivedAt)}</td>
      {spalten.map((c) => (
        <td key={c.key} className={`max-w-[28rem] whitespace-nowrap px-3 py-1.5 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}>
          {zelle(a, c.key)}
        </td>
      ))}
      {config && hinzu.length > 0 && <td />}
      <td />
    </tr>
  )

  const dateienGewaehlt = async (liste: FileList | null) => {
    const d = Array.from(liste ?? [])
    if (d.length) await ablegen(d)
    if (datei.current) datei.current.value = ''
  }
  const flaeche = darf ? ablageflaeche(setZiehen, (d) => void ablegen(d)) : {}

  const papierkorbLaden = async (): Promise<PapierkorbEintrag[]> => {
    const r = await fetchInbox({ trash: true, limit: 200, sortBy: 'receivedAt', sortDir: 'desc' })
    return r.data.map((x) => ({ id: x.id, name: x.fileName, deletedAt: x.deletedAt ?? '', deletedByName: null }))
  }

  return (
    <div className={`${pageWide} relative min-h-full`} {...flaeche}>
      {ziehen && (
        <div className="pointer-events-none absolute inset-2 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-primary/5 text-lg font-medium text-primary">
          <FolderInput className="mr-2 h-6 w-6" aria-hidden /> Loslassen legt die Dateien im Posteingang ab
        </div>
      )}
      {ordnerStoerung && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
          <span>
            <span className="font-medium">Ordner nicht verbunden{ordnerStoerung.folderErrorSince ? ` — seit ${eingangText(ordnerStoerung.folderErrorSince)}` : ''}.</span> {ordnerStoerung.folderError}
          </span>
          {einstellungen && (
            <Link to="/inbox/settings" className="underline">
              Zu den Einstellungen
            </Link>
          )}
        </div>
      )}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
            <Inbox className="h-7 w-7" aria-hidden /> Posteingang
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Angebote, Auftragsbestätigungen, Lieferscheine und Rechnungen — Dateien oder ganze E-Mails (.eml, .msg) hierher ziehen.
          </p>
        </div>
        <div className="relative flex items-center gap-2">
          {einstellungen && (
            <Link to="/inbox/settings" className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-card" title="Einstellungen des Posteingangs">
              <SlidersHorizontal className="h-4 w-4" /> Einstellungen
            </Link>
          )}
          {darf && (
            <>
              <input ref={datei} type="file" multiple className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.xml,.eml,.msg" onChange={(e) => void dateienGewaehlt(e.target.files)} />
              <button type="button" onClick={() => datei.current?.click()} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90">
                <Upload className="h-4 w-4" /> Dateien ablegen
              </button>
            </>
          )}
          {darf && (
            <button
              type="button"
              aria-expanded={korb}
              onClick={() => setKorb((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-card"
            >
              <Trash2 className="h-4 w-4" /> Papierkorb
            </button>
          )}
          {korb && (
            <PapierkorbFenster
              titel="Papierkorb des Posteingangs"
              aufbewahrung="30 Tage"
              laden={papierkorbLaden}
              onSchliessen={() => setKorb(false)}
              onWiederherstellen={(id) => {
                void restoreInboxItem(id)
                  .then(() => {
                    neu()
                    void zaehlerNeu()
                  })
                  .catch((e) => setError(apiErrorText(e, 'Wiederherstellen hat nicht geklappt.')))
              }}
            />
          )}
        </div>
      </div>

      <FilterBar
        availableFields={FILTER_FELDER}
        value={filterValues}
        onChange={onFilter}
        layoutKey="inbox"
        adresse={adresse}
        defaultFieldKeys={['q', 'status', 'kind', 'from']}
        defaultValues={{ status: { fieldKey: 'status', operator: 'is', value: 'UNASSIGNED,SUGGESTED,FAILED' } }}
      />

      {gewaehlt.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">{gewaehlt.length} gewählt</span>
          <button type="button" onClick={() => setZuordnenOffen(true)} className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-primary-foreground">
            <Link2 className="h-4 w-4" /> Zuordnen
          </button>
          <button type="button" onClick={() => void fuerAlle('Ignoriert', (a) => setInboxIgnored(a.id, true))} className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 hover:bg-muted">
            <EyeOff className="h-4 w-4" /> Ignorieren
          </button>
          <button type="button" onClick={() => void loeschenAlle()} className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-destructive hover:bg-destructive/10">
            <Trash2 className="h-4 w-4" /> Löschen
          </button>
        </div>
      )}
      {hinweis && (
        <div className="flex items-start justify-between rounded-md bg-muted px-4 py-2 text-sm">
          <span>{hinweis}</span>
          <button type="button" onClick={() => setHinweis(null)} aria-label="Hinweis schließen">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setConfig((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md ${config ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          title="Spalten konfigurieren"
        >
          {config ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />
        {config && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={LAYOUT_KEY}
              currentValue={colState}
              onReset={(v) => {
                const n = { ...VORGABE, ...((v as Partial<ColumnState> | null) ?? {}) }
                markUiSettingApplied(LAYOUT_KEY, n)
                setColState(n)
                void laden(filterValues, n)
              }}
            />
          </div>
        )}
        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="w-8 px-2 py-1.5">
                  {darf && (
                    <input
                      type="checkbox"
                      aria-label="alle wählen"
                      checked={rows.length > 0 && rows.every((x) => auswahl.has(x.id))}
                      onChange={(ev) => setAuswahl(ev.target.checked ? new Set(rows.map((x) => x.id)) : new Set())}
                    />
                  )}
                </th>
                <th className="relative px-3 py-1.5 text-left font-medium">
                  <button type="button" onClick={() => sortiere('receivedAt')} className="flex items-center gap-1 hover:text-primary">
                    Eingang {pfeil('receivedAt')}
                  </button>
                </th>
                {spalten.map((c) => (
                  <th
                    key={c.key}
                    className={`relative select-none px-3 py-1.5 font-medium ${c.align === 'right' ? 'text-right' : 'text-left'} ${config ? 'cursor-grab' : ''} ${dragOver === c.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={config}
                    onDragStart={config ? () => (dragKey.current = c.key) : undefined}
                    onDragOver={
                      config
                        ? (e) => {
                            e.preventDefault()
                            setDragOver(c.key)
                          }
                        : undefined
                    }
                    onDrop={
                      config
                        ? (e) => {
                            e.stopPropagation()
                            const q = dragKey.current
                            setDragOver(null)
                            if (!q || q === c.key) return
                            setze((p) => {
                              const k = [...p.columnKeys]
                              k.splice(k.indexOf(q), 1)
                              k.splice(k.indexOf(c.key), 0, q)
                              return { ...p, columnKeys: k }
                            })
                          }
                        : undefined
                    }
                    style={colState.columnWidths[c.key] ? { width: colState.columnWidths[c.key] } : undefined}
                  >
                    <div className={`flex items-center gap-1 ${c.align === 'right' ? 'justify-end' : ''}`}>
                      {c.sort ? (
                        <button type="button" onClick={() => sortiere(c.sort!)} className="flex items-center gap-1 hover:text-primary">
                          {c.label} {pfeil(c.sort)}
                        </button>
                      ) : (
                        c.label
                      )}
                      {config && (
                        <button type="button" className="ml-1 text-muted-foreground hover:text-destructive" onClick={() => setze((p) => ({ ...p, columnKeys: p.columnKeys.filter((k) => k !== c.key) }))}>
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => breite(e, c.key)} />
                  </th>
                ))}
                {config && hinzu.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs"
                      defaultValue=""
                      onChange={(e) => {
                        const k = e.target.value
                        if (k) setze((p) => ({ ...p, columnKeys: [...p.columnKeys, k] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ Spalte</option>
                      {hinzu.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={gesamt} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Laden…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={gesamt} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Nichts offen. Dateien oder ganze E-Mails hierher ziehen — oder auf das Symbol neben dem Logo, von jeder Seite aus.
                  </td>
                </tr>
              )}
              {zeilen.map((z, i) => {
                if (z.art === 'einzeln') return eintragZeile(z.eintrag, i)
                const text = z.eintraege.find((x) => x.isMailText) ?? z.eintraege[0]
                const auf = !zu.has(z.groupId)
                return [
                  <tr key={z.groupId} className={`border-b border-border ${i % 2 === 0 ? 'bg-background' : 'bg-muted/20'} hover:bg-accent/30`}>
                    <td className="w-8 px-2 py-1.5">
                      {darf && (
                        <input
                          type="checkbox"
                          aria-label="ganze E-Mail wählen"
                          checked={z.eintraege.every((x) => auswahl.has(x.id))}
                          onChange={(ev) => z.eintraege.forEach((x) => waehle(x.id, ev.target.checked))}
                        />
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-muted-foreground">
                      <button
                        type="button"
                        aria-expanded={auf}
                        aria-label={auf ? 'E-Mail zuklappen' : 'E-Mail aufklappen'}
                        className="mr-1 inline-flex align-middle text-muted-foreground hover:text-foreground"
                        onClick={() =>
                          setZu((alt) => {
                            const n = new Set(alt)
                            if (n.has(z.groupId)) n.delete(z.groupId)
                            else n.add(z.groupId)
                            return n
                          })
                        }
                      >
                        {auf ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                      {eingangText(text.receivedAt)}
                    </td>
                    <td colSpan={spalten.length + (config && hinzu.length ? 1 : 0) + 1} className="px-3 py-1.5">
                      <button type="button" className="inline-flex items-center gap-1.5 text-left hover:text-primary" onClick={() => setOffen(text.id)}>
                        <Mail className="h-4 w-4 text-muted-foreground" aria-hidden />
                        <span className="font-medium">{text.subject || 'E-Mail ohne Betreff'}</span>
                        <span className="text-muted-foreground">
                          · {text.senderName || text.senderAddress || 'unbekannt'} · {z.eintraege.length} Einträge
                        </span>
                      </button>
                    </td>
                  </tr>,
                  ...(auf ? z.eintraege.map((a) => eintragZeile(a, i, true)) : []),
                ]
              })}
              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td colSpan={gesamt} className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">
                    {rows.length < total ? `${rows.length} von ${total} Einträgen — Filter enger stellen` : `${total} ${total === 1 ? 'Eintrag' : 'Einträge'}`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {offen && <EintragSeitenfeld id={offen} onWahl={setOffen} onClose={() => setOffen(null)} onGeaendert={neu} />}
      {zuordnenOffen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-24" role="dialog" aria-modal="true" aria-label="Zuordnen" onClick={() => setZuordnenOffen(false)}>
          <div className="w-full max-w-lg rounded-lg border border-border p-4 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} onClick={(ev) => ev.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-semibold">{gewaehlt.length} Einträge einem Ziel zuordnen</h2>
              <button type="button" onClick={() => setZuordnenOffen(false)} aria-label="Schließen" className="rounded-md p-1 hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <ZielSuche
              onWahl={async (z) => {
                setZuordnenOffen(false)
                // Der Mailtext folgt seinem Anhang von selbst (PE-3) — ihn nur zuordnen, wenn er allein gewählt ist.
                const mitAnhang = new Set(gewaehlt.filter((x) => !x.isMailText).map((x) => x.groupId))
                await fuerAlle(`Zugeordnet zu „${z.label}"`, (a) =>
                  a.isMailText && mitAnhang.has(a.groupId) ? Promise.resolve() : assignInboxItem(a.id, z.targetKind, z.targetId),
                )
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
